/**
 * Generates percentage problems for Percentage Quest game.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const generateDistractors = (correct, count = 3) => {
    const seen = new Set([correct]);
    const distractors = [];
    const offsets = [1, 2, 5, 10, -1, -2, -5, -10];
    while (distractors.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        let d = correct + off;
        if (d < 0) d = Math.abs(d);
        if (d !== correct && !seen.has(d)) {
            seen.add(d);
            distractors.push(d);
        }
    }
    return distractors;
};

const gcd = (a, b) => {
    let x = Math.abs(a);
    let y = Math.abs(b);
    while (y) {
        [x, y] = [y, x % y];
    }
    return x || 1;
};

const formatToDecimals = (value, decimals) => {
    return Number(value.toFixed(decimals));
};

// Format number for display with at most one decimal place, avoiding long floats.
const formatOneDecimal = (value) => {
    const fixed = value.toFixed(1);
    return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
};

/**
 * Type A (all difficulties): direct \"What is X% of Y?\"
 * For easy, ensures whole-number answers with friendly percentages.
 */
export const generatePercentOfQuestion = (difficulty = "normal") => {
    let pct;
    let whole;
    let answer;

    if (difficulty === "easy") {
        // Multiples of 5 or 10 with whole-number answers
        const pctPool = [5, 10, 15, 20, 25, 30, 40, 50];
        pct = pctPool[Math.floor(Math.random() * pctPool.length)];
        const denom = 100 / gcd(100, pct); // smallest whole so that pct% of whole is integer
        const k = Math.floor(Math.random() * 10) + 2; // 2-11
        whole = denom * k;
        answer = (pct / 100) * whole; // guaranteed whole number
    } else if (difficulty === "hard") {
        // Any pct 5-80 on larger wholes, allow decimals (1 d.p. for display)
        pct = Math.floor(Math.random() * 76) + 5; // 5-80
        whole = Math.floor(Math.random() * 181) + 40; // 40-220
        answer = (pct / 100) * whole;
        answer = formatToDecimals(answer, 1);
    } else {
        // Medium: 1–50%, any whole number, 1 decimal allowed
        pct = Math.floor(Math.random() * 50) + 1; // 1-50
        whole = Math.floor(Math.random() * 181) + 20; // 20-200
        answer = (pct / 100) * whole;
        answer = formatToDecimals(answer, 1);
    }

    const distractors = generateDistractors(answer);
    const formattedAnswer = formatOneDecimal(answer);
    const options = shuffle([formattedAnswer, ...distractors.map(formatOneDecimal)]);

    return {
        mode: 'percent-of',
        question: `What is ${pct}% of ${whole}?`,
        answer: formattedAnswer,
        options,
        type: 'percent',
    };
};

/**
 * Type B: X is what percent of Y?
 */
export const generateWhatPercentQuestion = (difficulty = "normal") => {
    let minPct = 10;
    let maxPct = 40;
    if (difficulty === "easy") {
        maxPct = 30;
    } else if (difficulty === "hard") {
        maxPct = 80;
    }
    const percent =
        Math.floor(Math.random() * (maxPct - minPct + 1)) + minPct;

    let wholeMin = 40;
    let wholeMax = 150;
    if (difficulty === "easy") {
        wholeMax = 100;
    } else if (difficulty === "hard") {
        wholeMin = 80;
        wholeMax = 300;
    }
    const whole = Math.floor(Math.random() * (wholeMax - wholeMin + 1)) + wholeMin;
    const part = Math.round((percent / 100) * whole);
    const answer = percent;
    const distractors = generateDistractors(percent);
    const options = shuffle([String(answer), ...distractors.map(String)]);

    return {
        mode: 'what-percent',
        question: `${part} is what percent of ${whole}?`,
        answer: String(answer),
        options,
        type: 'percent',
    };
};

/**
 * Type C: X% of ? = Z (find the whole) - numeric answer
 */
export const generateFindWholeQuestion = (difficulty = "normal") => {
    const easyPcts = [10, 25, 50];
    const hardPcts = [10, 15, 20, 25, 30, 40, 50];
    const pctPool =
        difficulty === "easy" ? easyPcts : difficulty === "hard" ? hardPcts : [10, 20, 25, 50];
    const pct = pctPool[Math.floor(Math.random() * pctPool.length)];

    let wholeMin = 20;
    let wholeMax = 100;
    if (difficulty === "easy") {
        wholeMax = 80;
    } else if (difficulty === "hard") {
        wholeMin = 60;
        wholeMax = 240;
    }
    const whole = Math.floor(Math.random() * (wholeMax - wholeMin + 1)) + wholeMin;
    const part = Math.round((pct / 100) * whole);
    const answer = whole;
    const distractors = generateDistractors(whole);
    const options = shuffle([String(answer), ...distractors.map(String)]);

    return {
        mode: 'find-whole',
        question: `${pct}% of what number equals ${part}?`,
        answer: String(answer),
        options,
        type: 'percent',
    };
};

/**
 * Medium & Hard: one-step increase/decrease word problems.
 */
const generateIncreaseDecreaseQuestion = (difficulty = "normal") => {
    const base = Math.floor(Math.random() * 181) + 20; // 20-200
    const pct = Math.floor(Math.random() * 46) + 5; // 5-50
    const isIncrease = Math.random() < 0.5;

    const factor = 1 + (isIncrease ? pct : -pct) / 100;
    let result = base * factor;
    result = formatToDecimals(result, 1);

    const distractors = generateDistractors(result);
    const formattedAnswer = formatOneDecimal(result);
    const options = shuffle([formattedAnswer, ...distractors.map(formatOneDecimal)]);

    const direction = isIncrease ? "increases" : "decreases";

    return {
        mode: 'change',
        question: `The price of a toy is £${base}. It ${direction} by ${pct}%. What is the new price?`,
        answer: formattedAnswer,
        options,
        type: 'percent',
    };
};

/**
 * Hard: reverse percentage (work backwards from final value).
 */
const generateReversePercentQuestion = () => {
    const original = Math.floor(Math.random() * 181) + 40; // 40-220
    const pct = Math.floor(Math.random() * 36) + 5; // 5-40
    const isDiscount = Math.random() < 0.5;
    const factor = 1 + (isDiscount ? -pct : pct) / 100;
    const final = formatToDecimals(original * factor, 1);

    const answer = formatToDecimals(original, 1);
    const distractors = generateDistractors(answer);
    const formattedFinal = formatOneDecimal(final);
    const formattedAnswer = formatOneDecimal(answer);
    const options = shuffle([formattedAnswer, ...distractors.map(formatOneDecimal)]);

    const phrase = isDiscount ? "after a discount of" : "after an increase of";

    return {
        mode: 'reverse',
        question: `A jacket costs £${formattedFinal} ${phrase} ${pct}%. What was the original price?`,
        answer: formattedAnswer,
        options,
        type: 'percent',
    };
};

/**
 * Hard: multi-step percentage word problem.
 */
const generateMultiStepPercentQuestion = () => {
    const start = Math.floor(Math.random() * 181) + 40; // 40-220
    const increase = Math.floor(Math.random() * 21) + 5; // 5-25
    const decrease = Math.floor(Math.random() * 21) + 5; // 5-25

    const afterIncrease = start * (1 + increase / 100);
    const final = formatToDecimals(afterIncrease * (1 - decrease / 100), 1);

    const answer = final;
    const distractors = generateDistractors(final);
    const formattedAnswer = formatOneDecimal(answer);
    const options = shuffle([formattedAnswer, ...distractors.map(formatOneDecimal)]);

    return {
        mode: 'multi-step',
        question: `A number is increased by ${increase}% and then decreased by ${decrease}% to get ${formattedAnswer}. What was the original number?`,
        answer: formattedAnswer,
        options,
        type: 'percent',
    };
};

/**
 * Pick a random percentage question type.
 */
export const generatePercentageQuestion = (difficulty = "normal") => {
    if (difficulty === "easy") {
        // Easy: simple percent-of with whole-number answers only
        return generatePercentOfQuestion("easy");
    }

    if (difficulty === "hard") {
        // Hard: reverse and multi-step word problems plus some direct calculations
        const fns = [generatePercentOfQuestion, generateFindWholeQuestion, generateIncreaseDecreaseQuestion, generateReversePercentQuestion, generateMultiStepPercentQuestion];
        const fn = fns[Math.floor(Math.random() * fns.length)];
        return fn("hard");
    }

    // Medium: 1–50% range, direct and increase/decrease
    const fns = [generatePercentOfQuestion, generateIncreaseDecreaseQuestion];
    const fn = fns[Math.floor(Math.random() * fns.length)];
    return fn("normal");
};
